import React, { useState, useEffect, useCallback } from 'react';
import { useChat } from '../contexts/ChatContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Search, Hash, MessageSquare, User } from 'lucide-react';

const SearchDialog = ({ open, onClose }) => {
  const { searchMessages, setActiveChannel } = useChat();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const msgs = await searchMessages(q);
      setResults(msgs);
    } catch (e) {
      console.error('Search error:', e);
    }
    setSearching(false);
  }, [searchMessages]);

  useEffect(() => {
    const timer = setTimeout(() => handleSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query, handleSearch]);

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); }
  }, [open]);

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="global-search">
        <DialogHeader>
          <DialogTitle style={{fontFamily: 'Space Grotesk'}}>Search Messages</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search messages..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            autoFocus
            data-testid="global-search-input"
          />
        </div>
        <ScrollArea className="max-h-[400px]">
          {searching ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Searching...</div>
          ) : results.length === 0 && query ? (
            <div className="py-8 text-center text-sm text-muted-foreground">No results found</div>
          ) : (
            <div className="space-y-1">
              {results.map(msg => (
                <button
                  key={msg.message_id}
                  className="w-full text-left p-3 rounded-lg hover:bg-muted/50 transition-colors"
                  onClick={onClose}
                  data-testid="global-search-result"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-medium">
                      {msg.sender_name?.[0] || '?'}
                    </div>
                    <span className="text-sm font-medium">{msg.sender_name}</span>
                    <span className="text-xs text-muted-foreground">{formatTime(msg.timestamp)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{msg.content}</p>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
